import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Copy,
  Key,
  HandHelping,
  Trash2,
  Edit3,
  Check,
  Sparkles,
  Plus,
  Crown,
} from 'lucide-react';
import { formatLocalDate } from '../utils/date';
import GroupPrayerComments from '../components/GroupPrayerComments';

/**
 * PrayerGroup — dedicated page for a single private group.
 * Shows: group header, member list, and the private prayer feed with
 * the same actions available on the main Prayer Wall (post, pray, edit,
 * delete, mark answered). Group prayers auto-approve (no moderation).
 */
const PrayerGroup = () => {
  const { id: groupId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);

  const [group, setGroup] = useState(null);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [prayers, setPrayers] = useState([]);
  const [loadingPrayers, setLoadingPrayers] = useState(true);

  // Post prayer dialog
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [newPrayerText, setNewPrayerText] = useState('');
  const [newPrayerType, setNewPrayerType] = useState('ongoing');
  const [posting, setPosting] = useState(false);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPrayer, setEditingPrayer] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [prayingId, setPrayingId] = useState(null);

  const fetchGroup = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups/${groupId}`);
      setGroup(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('You are not a member of this group');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load group');
      }
      navigate('/prayers?tab=groups');
    } finally {
      setLoadingGroup(false);
    }
  }, [API, groupId, navigate]);

  const fetchPrayers = useCallback(async () => {
    setLoadingPrayers(true);
    try {
      const res = await axios.get(`${API}/prayers/group/${groupId}`);
      setPrayers(res.data || []);
    } catch (e) {
      // Silent — the group-load error handler will already redirect if 403
    } finally {
      setLoadingPrayers(false);
    }
  }, [API, groupId]);

  useEffect(() => {
    fetchGroup();
    fetchPrayers();
  }, [fetchGroup, fetchPrayers]);

  const handlePost = async () => {
    const text = newPrayerText.trim();
    if (!text) {
      toast.error('Please write your prayer');
      return;
    }
    setPosting(true);
    try {
      await axios.post(`${API}/prayers`, {
        prayer_text: text,
        visibility: 'group',
        group_id: groupId,
        prayer_type: newPrayerType,
      });
      toast.success('Prayer shared with your group.');
      setNewPrayerText('');
      setNewPrayerType('ongoing');
      setPostDialogOpen(false);
      fetchPrayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post prayer');
    } finally {
      setPosting(false);
    }
  };

  const handlePray = async (prayer) => {
    if (prayingId === prayer.id) return;
    setPrayingId(prayer.id);
    try {
      const res = await axios.post(`${API}/prayers/${prayer.id}/pray`);
      const { prayer_count, has_prayed } = res.data || {};
      setPrayers((prev) =>
        prev.map((p) =>
          p.id === prayer.id
            ? {
                ...p,
                prayer_count,
                prayer_user_ids: has_prayed
                  ? Array.from(new Set([...(p.prayer_user_ids || []), user.id]))
                  : p.prayer_user_ids,
              }
            : p,
        ),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record prayer');
    } finally {
      setPrayingId(null);
    }
  };

  const handleMarkAnswered = async (prayer, answered) => {
    try {
      await axios.put(`${API}/prayers/${prayer.id}`, { answered });
      toast.success(answered ? 'Marked as answered! Praise God!' : 'Unmarked as answered');
      fetchPrayers();
    } catch {
      toast.error('Failed to update prayer');
    }
  };

  const handleDelete = async (prayer) => {
    if (!window.confirm('Delete this prayer?')) return;
    try {
      await axios.delete(`${API}/prayers/${prayer.id}`);
      toast.success('Prayer deleted');
      fetchPrayers();
    } catch {
      toast.error('Failed to delete prayer');
    }
  };

  const openEdit = (prayer) => {
    setEditingPrayer(prayer);
    setEditText(prayer.prayer_text || '');
    setEditDialogOpen(true);
  };

  const submitEdit = async () => {
    if (!editingPrayer) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error('Prayer text cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/prayers/${editingPrayer.id}`, { prayer_text: trimmed });
      toast.success('Prayer updated');
      setEditDialogOpen(false);
      fetchPrayers();
    } catch {
      toast.error('Failed to update prayer');
    } finally {
      setSavingEdit(false);
    }
  };

  const copyCode = async () => {
    if (!group?.invite_code) return;
    try {
      await navigator.clipboard.writeText(group.invite_code);
      toast.success(`Invite code ${group.invite_code} copied`);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  if (loadingGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <p className="text-slate-500">Loading group…</p>
        </div>
      </div>
    );
  }

  if (!group) return null;

  const ownerId = group.created_by;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/prayers?tab=groups')}
          className="mb-4 text-[#2B6B7F]"
          data-testid="back-to-prayers"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Groups
        </Button>

        {/* Group Header */}
        <Card className="mb-6 border-t-4 border-t-[#2B6B7F]" data-testid="prayer-group-header">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <CardTitle className="text-2xl text-[#2B6B7F] flex items-center gap-2 flex-wrap">
                  <Users className="w-6 h-6" />
                  {group.name}
                </CardTitle>
                {group.description && (
                  <CardDescription className="mt-1">{group.description}</CardDescription>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  {group.member_count} member{group.member_count === 1 ? '' : 's'} • Private group
                </p>
              </div>

              <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#2B6B7F]" data-testid="post-group-prayer-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Post Prayer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Share a prayer with {group.name}</DialogTitle>
                    <DialogDescription>
                      Group prayers post immediately — only members of this group can see them.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block text-slate-700">Type</label>
                      <Select value={newPrayerType} onValueChange={setNewPrayerType}>
                        <SelectTrigger data-testid="new-group-prayer-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="ongoing">Ongoing</SelectItem>
                          <SelectItem value="short_term">Short Term</SelectItem>
                          <SelectItem value="praise">Praise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      placeholder="What would you like this group to pray for?"
                      value={newPrayerText}
                      onChange={(e) => setNewPrayerText(e.target.value)}
                      rows={5}
                      data-testid="new-group-prayer-textarea"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setPostDialogOpen(false)}
                      disabled={posting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePost}
                      disabled={posting}
                      className="bg-[#2B6B7F]"
                      data-testid="submit-group-prayer-btn"
                    >
                      {posting ? 'Posting…' : 'Post Prayer'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {group.is_owner && group.invite_code && (
              <button
                type="button"
                onClick={copyCode}
                className="mt-3 w-full sm:w-auto sm:self-start flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid="group-invite-code-btn"
              >
                <Key className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs text-slate-600">Invite code</span>
                <span className="font-mono font-semibold tracking-widest text-[#2B6B7F] flex items-center gap-1.5">
                  {group.invite_code}
                  <Copy className="w-3 h-3 opacity-60" />
                </span>
              </button>
            )}
          </CardHeader>
        </Card>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Prayer Feed */}
          <div className="lg:col-span-2 space-y-4" data-testid="group-prayer-feed">
            <h2 className="text-lg font-semibold text-slate-800">Prayer Feed</h2>
            {loadingPrayers ? (
              <p className="text-sm text-slate-500">Loading prayers…</p>
            ) : prayers.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-slate-500 text-sm">
                  No prayers in this group yet. Tap <strong>Post Prayer</strong> to start.
                </CardContent>
              </Card>
            ) : (
              prayers.map((p) => {
                const isAuthor = user && p.user_id === user.id;
                const hasPrayed = user && (p.prayer_user_ids || []).includes(user.id);
                return (
                  <Card
                    key={p.id}
                    className="border-slate-200"
                    data-testid={`group-prayer-card-${p.id}`}
                  >
                    <CardContent className="pt-5">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="font-medium text-slate-800">{p.user_name}</p>
                          <p className="text-xs text-slate-500">
                            {formatLocalDate(p.created_at, { month: 'short', day: 'numeric' })}
                            {p.prayer_type && p.prayer_type !== 'petition' && ` • ${p.prayer_type}`}
                          </p>
                        </div>
                        {p.answered && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 flex-shrink-0">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Praise Report
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">
                        {p.prayer_text}
                      </p>

                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant={hasPrayed ? 'default' : 'outline'}
                          disabled={isAuthor || prayingId === p.id}
                          onClick={() => handlePray(p)}
                          className={hasPrayed ? 'bg-[#2B6B7F] hover:bg-[#245968]' : ''}
                          data-testid={`group-page-pray-btn-${p.id}`}
                        >
                          <HandHelping className="w-3.5 h-3.5 mr-1" />
                          I prayed for this
                          {isAuthor &&
                            typeof p.prayer_count === 'number' &&
                            p.prayer_count > 0 && (
                              <span className="ml-1.5 text-xs opacity-80">
                                ({p.prayer_count})
                              </span>
                            )}
                        </Button>

                        {isAuthor && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkAnswered(p, !p.answered)}
                              data-testid={`group-page-answered-btn-${p.id}`}
                            >
                              <Check className="w-3.5 h-3.5 mr-1" />
                              {p.answered ? 'Unmark' : 'Mark Answered'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEdit(p)}
                              data-testid={`group-page-edit-btn-${p.id}`}
                            >
                              <Edit3 className="w-3.5 h-3.5 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(p)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`group-page-delete-btn-${p.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>

                      <GroupPrayerComments prayer={p} />
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* Members List */}
          <div className="lg:col-span-1">
            <Card data-testid="group-members-list">
              <CardHeader>
                <CardTitle className="text-lg text-[#2B6B7F] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Members ({group.member_count})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {group.members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50"
                    data-testid={`group-member-${m.id}`}
                  >
                    <span className="text-sm text-slate-700 truncate">{m.name}</span>
                    {m.id === ownerId && (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-amber-300 text-amber-700 bg-amber-50"
                      >
                        <Crown className="w-2.5 h-2.5 mr-0.5" />
                        Owner
                      </Badge>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Edit dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Edit your prayer</DialogTitle>
              <DialogDescription>
                Group prayers update immediately — no admin approval needed.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={5}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              data-testid="group-page-edit-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button
                onClick={submitEdit}
                disabled={savingEdit}
                className="bg-[#2B6B7F]"
                data-testid="group-page-edit-save-btn"
              >
                {savingEdit ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PrayerGroup;
